import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Shield, CheckCircle2, AlertTriangle, XCircle, RefreshCw } from 'lucide-react';

const STATUS_ICONS = {
  operational: { icon: CheckCircle2, color: 'text-emerald-500', bg: 'bg-emerald-100', label: 'Operational' },
  minor_issues: { icon: AlertTriangle, color: 'text-amber-500', bg: 'bg-amber-100', label: 'Minor Issues' },
  degraded: { icon: AlertTriangle, color: 'text-red-500', bg: 'bg-red-100', label: 'Degraded' },
  unavailable: { icon: XCircle, color: 'text-gray-400', bg: 'bg-gray-100', label: 'Unavailable' },
  unknown: { icon: RefreshCw, color: 'text-gray-400', bg: 'bg-gray-100', label: 'Unknown' },
};

export default function StatusPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data: res } = await api.get('/api/enhance/status');
        setData(res);
      } catch (e) { /* handled */ }
      finally { setLoading(false); }
    })();
  }, []);

  const overall = STATUS_ICONS[data?.overall_status] || STATUS_ICONS.unknown;
  const OverallIcon = overall.icon;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="status-page">
      {/* Header */}
      <div className="bg-[#0F172A] text-white py-8">
        <div className="max-w-3xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-[#D4A843] rounded-lg flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <h1 className="font-heading text-2xl font-bold tracking-tight">TechOps Status</h1>
          </div>
          {loading ? (
            <div className="flex items-center gap-2"><RefreshCw className="w-4 h-4 animate-spin text-gray-400" /><span className="text-sm text-gray-400">Loading...</span></div>
          ) : (
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg ${overall.bg}`} data-testid="status-overall">
              <OverallIcon className={`w-5 h-5 ${overall.color}`} />
              <span className={`text-sm font-bold ${overall.color}`}>{overall.label}</span>
            </div>
          )}
        </div>
      </div>

      {/* Services */}
      {data && (
        <div className="max-w-3xl mx-auto px-6 py-8 space-y-4">
          <h2 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-4">Service Status</h2>
          <div className="space-y-2" data-testid="status-services">
            {(data.services || []).map(service => {
              const s = STATUS_ICONS[service.status] || STATUS_ICONS.unknown;
              const Icon = s.icon;
              return (
                <div key={service.name} className="bg-white border border-gray-200 rounded-lg p-4 flex items-center justify-between" data-testid={`status-svc-${service.name}`}>
                  <p className="text-sm font-medium text-gray-900">{service.name}</p>
                  <div className="flex items-center gap-2">
                    <Icon className={`w-4 h-4 ${s.color}`} />
                    <span className={`text-xs font-bold ${s.color}`}>{s.label}</span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Metrics */}
          <div className="mt-8">
            <h2 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-4">Platform Metrics</h2>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="status-metrics">
              {[
                { label: 'Total Tickets', value: data.metrics?.total_tickets || 0 },
                { label: 'Open Tickets', value: data.metrics?.open_tickets || 0 },
                { label: 'SCADA Devices', value: data.metrics?.scada_devices || 0 },
                { label: 'Devices Online', value: data.metrics?.scada_online || 0 },
                { label: 'Active Alerts', value: data.metrics?.active_alerts || 0 },
              ].map(m => (
                <div key={m.label} className="bg-white border border-gray-200 rounded-lg p-4 text-center">
                  <p className="text-xl font-bold text-gray-900">{m.value}</p>
                  <p className="text-[10px] text-gray-500 mt-1">{m.label}</p>
                </div>
              ))}
            </div>
          </div>

          <p className="text-[10px] text-gray-400 text-center mt-8">
            Last updated: {data.last_updated ? new Date(data.last_updated).toLocaleString() : '—'} | TechOps — EIGOO Inc
          </p>
        </div>
      )}
    </div>
  );
}
